import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { makeStyles } from '@mui/styles';
import { IconButton, Tooltip, Paper, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import ImageIcon from '@mui/icons-material/Image';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import moment from 'moment';
import { ToggleDeleteAttachmentDialog, DownloadAttachment } from '../../../../../../actions/SiteTrackerActions.js';

const useStyles = makeStyles((theme) => ({
	root: {},
	content: {
		padding: 0,
	},
	inner: {
		minWidth: 700,
	},
	nameCell: {
		display: 'flex',
		alignItems: 'center',
	},
	avatar: {
		height: 42,
		width: 42,
		marginRight: theme.spacing(1),
	},
	actions: {
		padding: theme.spacing(1),
		justifyContent: 'flex-end',
	},
	primaryButton: {
		color: theme.palette.primary.main
	},
	errorButton: {
		color: theme.palette.error.main
	},
	infoButton: {
		color: theme.palette.info.main
	}
}));

const TeamsFilesDatatable = (props) => {
	const { className, data, tableId, ...rest } = props;
	const dispatch = useDispatch();
	const classes = useStyles();
	const userId = useSelector(state => state.session.user.id);

	let dataChecked = data || [];

	if (data && data.length === 0) {
		dataChecked = [
			{ id: 1, filename: 'No attachments added', fileBase64: 'N/A' },
			{ id: 2, userName: ' ', fileBase64: 'N/A' },
			{ id: 3, userName: ' ', fileBase64: 'N/A' }];
	}

	const _downloadFile = (rowId) => {
		dispatch(DownloadAttachment(userId, rowId));
	};

	const _deleteAttachment = (rowId) => {
		dispatch(ToggleDeleteAttachmentDialog(true, tableId, rowId));
	};

	const _renderDate = (date) => {
		if (date !== undefined) {
			return (
				moment(date).format('YYYY-MM-DD HH:mm:ss')
			);
		}

		return ('');
	};

	const _openFileImage = (fileBase64) => {
		const imgElement = new Image();

		imgElement.onload = function () {
			const newTab = window.open();
			if (newTab && newTab.document) {
				newTab.document.body.appendChild(imgElement);
			}
		};
		imgElement.src = fileBase64;
	};

	const _openFileTxt = (fileBase64) => {
		const decodedText = atob(fileBase64);

		const newTab = window.open();
		newTab.document.body.innerText = decodedText;
	};

	const _renderImage = (fileBase64) => (
		<>
			<Tooltip title="Open">
				<span>
					<IconButton
						edge="end"
						size="small"
						className={classes.primaryButton}
						onClick={() => _openFileImage(fileBase64)}
					>
						<ImageIcon />
					</IconButton>
				</span>
			</Tooltip>
		</>
	);

	const _renderText = (fileBase64) => (
		<>
			<Tooltip title="Open">
				<span>
					<IconButton
						edge="end"
						size="small"
						className={classes.primaryButton}
						onClick={() => _openFileTxt(fileBase64)}
					>
						<TextSnippetIcon />
					</IconButton>
				</span>
			</Tooltip>
		</>
	);

	const _renderNothing = (fileBase64) => (
		<>
			<Tooltip title="Open">
				<span>
					<IconButton
						edge="end"
						size="small"
						className={classes.primaryButton}
						disabled
					>
						<InsertDriveFileIcon />
					</IconButton>
				</span>
			</Tooltip>
		</>
	);

	const _renderFiles = (fileBase64) => {
		if (fileBase64) {
			if (fileBase64 === 'N/A') {
				return ('');
			}
			if (fileBase64.includes('png')) {
				return (
					_renderImage(fileBase64)
				);
			}
			if (!fileBase64.includes('png')) {
				return (
					_renderText(fileBase64)
				);
			}
		}
		return (
			_renderNothing(fileBase64)
		);
	};

	const _renderActions = (rowId) => (
		<>
			<div hidden={rowId === undefined || (dataChecked && dataChecked[0].filename === 'No attachments added')}>
				<Tooltip title="Download">
					<IconButton
						edge="end"
						size="small"
						className={classes.primaryButton}
						onClick={() => _downloadFile(rowId)}
					>
						<CloudDownloadOutlinedIcon />
					</IconButton>
				</Tooltip>
				<Tooltip title="Remove">
					<IconButton
						edge="end"
						size="small"
						className={classes.errorButton}
						onClick={() => _deleteAttachment(rowId)}
					>
						<CancelIcon />
					</IconButton>
				</Tooltip>
			</div>
		</>
	);

	const divStyle = {
		overflowY: 'scroll',
		height: '20vh',
		position: 'relative'
	};

	return (
		<div className={clsx(classes.root, className)} style={dataChecked.length > 4 ? divStyle : []}>
			<TableContainer component={Paper}>
				<Table size="small" aria-label="a dense table">
					<TableBody>
						{dataChecked.map((row) => (
							<TableRow
								key={row.id}
								sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
							>
								<TableCell align="left">{row.userName}</TableCell>
								<TableCell align="left">{_renderDate(row.date)}</TableCell>
								<TableCell align="left">{row.filename}</TableCell>
								<TableCell align="center">
									{_renderFiles(row.fileBase64)}
								</TableCell>
								<TableCell align="center">
									{_renderActions(row.id)}
								</TableCell>

							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		</div>
	);
};

TeamsFilesDatatable.propTypes = {
	className: PropTypes.string,
	data: PropTypes.array,
	tableId: PropTypes.any,
};

export default TeamsFilesDatatable;
